import { IconButton, Stack, Switch, SwitchProps, Tooltip } from '@mui/material'
import { Device, Project, RuuviTag, User, UserAlertRule } from '../types'
import { usePolicy } from '../hooks'
import { InfoRounded, Warning } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { ChangeEvent, useMemo, useState } from 'react'
import { RootState } from '../redux'
import { useDispatch, useSelector } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { setUserAlertRules } from '../redux/slices/users'
import useUserAlertRules from '../hooks/useUserAlertRules'
import UserAlertRuleInput from '../types/UserAlertRuleInput'

export interface UserAlertRuleSmsNotificationsSwitchProps extends Omit<
  SwitchProps,
  'checked' | 'onChange'
> {
  user: User
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  userAlertRule: UserAlertRule
  userAlertRules?: UserAlertRule[]
  useRedux?: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void
  beforeSubmit?: (input: UserAlertRuleInput) => void
  afterSubmit?: (userAlertRules: UserAlertRule[]) => void
}

export default function UserAlertRuleSmsNotificationsSwitch({
  user,
  project,
  device,
  ruuviTag,
  userAlertRule,
  userAlertRules: initialUserAlertRules = [],
  useRedux = true,
  disabled,
  size = 'small',
  onChange,
  beforeSubmit,
  afterSubmit,
  ...props
}: UserAlertRuleSmsNotificationsSwitchProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy state.
   */
  const { canEditUserAlertRules } = usePolicy()

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * The user alert rules from redux.
   */
  const userAlertRulesFromRedux = useSelector((state: RootState) => state.users.userAlertRules)

  /**
   * The used user alert rules.
   */
  const userAlertRules = useRedux ? userAlertRulesFromRedux : initialUserAlertRules

  const { updateUserAlertRules } = useUserAlertRules({
    user,
    userAlertRules,
    project,
    device,
    ruuviTag,
    useRedux
  })

  /**
   * Check if the SMS notifications are enabled.
   */
  const smsNotificationsAreEnabled = user.notificationsBySms

  /**
   * Indicates if the SMS notification can be disabled.
   */
  const canDisableSmsNotifications = useMemo(() => {
    if (device) {
      if (device?.asset?.projectId !== undefined && device.asset.projectId !== null) {
        const projectRule = userAlertRules.find(
          (rule) =>
            rule.alertRuleName === userAlertRule.alertRuleName &&
            rule.projectId === device.asset?.projectId
        )

        if (projectRule && projectRule.smsNotificationEnabled) {
          return false
        }
      }

      return true
    }

    return true
  }, [device, userAlertRules])

  /**
   * Handle the "change" event.
   */
  async function handleChange(event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    try {
      setLoading(true)

      const input: UserAlertRuleInput = {
        ...userAlertRule,
        smsNotificationEnabled: checked
      }

      beforeSubmit?.(input)

      const updatedUserAlertRules = await updateUserAlertRules(input)

      afterSubmit?.(updatedUserAlertRules)

      dispatch(
        createSuccessNotification(
          checked ? t('enableSmsNotificationSuccess') : t('disableSmsNotificationSuccess')
        )
      )

      onChange?.(event, checked)
    } catch (_error) {
      dispatch(
        createErrorNotification(
          checked ? t('enableSmsNotificationError') : t('disableSmsNotificationError')
        )
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {canEditUserAlertRules(user) && (
        <>
          {smsNotificationsAreEnabled ? (
            <>
              {canDisableSmsNotifications ? (
                <Switch
                  value={userAlertRule.smsNotificationEnabled}
                  checked={userAlertRule.smsNotificationEnabled}
                  disabled={disabled || loading}
                  size={size}
                  {...props}
                  onChange={handleChange}
                />
              ) : (
                <Stack direction="row" alignItems="center" gap={1} sx={{ position: 'relative' }}>
                  <Switch value={true} checked={true} disabled={true} size={size} {...props} />
                  <Tooltip title={t('alert:notificationSettingCannotBeDisabled')}>
                    <IconButton
                      size="small"
                      color="warning"
                      sx={{ position: 'absolute', right: -28 }}
                    >
                      <InfoRounded fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </>
          ) : (
            <Tooltip title={t('smsNotificationsDisabled')}>
              <IconButton size={size} color="warning">
                <Warning />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </>
  )
}
